import { t } from '@lingui/core/macro';
import { useCallback, useEffect, useRef } from 'react';

import SolAPIError from '@/error/SolAPIError';
import { useNotification } from '@/providers/Notification';

type Props = {
  startSession: () => Promise<{ success: boolean }>;
  refreshSession: () => Promise<{ success: boolean }>;
  endSession: () => Promise<{ success: boolean }>;
  keepAlivePingInterval: number;
};

const useSessionLock = ({
  startSession,
  refreshSession,
  endSession,
  keepAlivePingInterval,
}: Props) => {
  const { pushNotification } = useNotification();
  const sessionIntervalId: { current: NodeJS.Timeout | null } = useRef(null);

  const startSessionLock = async (): Promise<boolean> => {
    try {
      const response = await startSession();
      if (!response.success) {
        throw new Error();
      }
      clearInterval(sessionIntervalId.current as NodeJS.Timeout);
      sessionIntervalId.current = setInterval(refreshSession, keepAlivePingInterval);

      return true;
    } catch (e) {
      let errMessage = t`Apologies, but something went wrong. Please refresh the page and try to edit again.`;
      if (e instanceof SolAPIError) {
        errMessage = e.getFirstError().message;
      }

      pushNotification({ type: 'error', message: errMessage });
    }

    return false;
  };

  const endSessionLock = useCallback(async (): Promise<boolean> => {
    clearInterval(sessionIntervalId.current as NodeJS.Timeout);
    const response = await endSession();
    return response.success;
  }, [endSession]);

  useEffect(() => {
    return () => {
      // End session on unmount
      endSessionLock();
    };
  }, [endSessionLock]);

  return {
    startSessionLock,
    endSessionLock,
  };
};

export default useSessionLock;
